import React, { useState, useMemo } from 'react';
import { functions2D } from '../../data/functions2d';
import { functions3D } from '../../data/functions3d';
import { MathItem } from '../../types/math';
import { Plot2D } from '../renderers/Plot2D';
import { Plot3D } from '../renderers/Plot3D';
import { MathFormula } from '../game/MathFormula';
import { Sliders, Sparkles, Layers, Box, Info } from 'lucide-react';

export const EquationLab: React.FC = () => {
  const [selectedDimension, setSelectedDimension] = useState<'2d' | '3d'>('2d');
  const [selectedId, setSelectedId] = useState<string>(functions2D[0].id);

  // Parameter values state
  const [params, setParams] = useState<Record<string, number>>({});

  const allItems = useMemo(() => {
    return selectedDimension === '2d' ? functions2D : functions3D;
  }, [selectedDimension]);

  const currentItem: MathItem = useMemo(() => {
    const found = allItems.find((it) => it.id === selectedId);
    return found || allItems[0];
  }, [allItems, selectedId]);

  // Handle item change & reset parameters
  const handleItemSelect = (item: MathItem) => {
    setSelectedId(item.id);
    const initialParams: Record<string, number> = {};
    if (item.parameters) {
      for (const [key, pDef] of Object.entries(item.parameters)) {
        initialParams[key] = pDef.default;
      }
    }
    setParams(initialParams);
  };

  const handleDimensionChange = (dim: '2d' | '3d') => {
    setSelectedDimension(dim);
    const nextItems = dim === '2d' ? functions2D : functions3D;
    handleItemSelect(nextItems[0]);
  };

  const handleParamChange = (key: string, value: number) => {
    setParams((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  // Get dynamic formula LaTeX if available
  const displayFormula = useMemo(() => {
    if (currentItem.dynamicFormulaLatex && Object.keys(params).length > 0) {
      return currentItem.dynamicFormulaLatex(params);
    }
    return currentItem.formulaLatex;
  }, [currentItem, params]);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-slate-900/60 p-4 rounded-xl border border-slate-800">
        <div>
          <h2 className="text-xl font-semibold text-white flex items-center gap-2">
            <Sliders className="w-5 h-5 text-teal-400" />
            Intuition Lab: Parameter Morphing
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            Adjust algebraic coefficients in real time to observe geometric transformations.
          </p>
        </div>

        {/* 2D / 3D Toggle */}
        <div className="flex items-center gap-2 bg-slate-950 p-1 rounded-lg border border-slate-800">
          <button
            onClick={() => handleDimensionChange('2d')}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition flex items-center gap-1.5 ${
              selectedDimension === '2d'
                ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5" /> 2D Curves
          </button>
          <button
            onClick={() => handleDimensionChange('3d')}
            className={`px-3 py-1.5 rounded-md text-xs font-mono font-medium transition flex items-center gap-1.5 ${
              selectedDimension === '3d'
                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Box className="w-3.5 h-3.5" /> 3D Surfaces
          </button>
        </div>
      </div>

      {/* Grid: Graph Viewport + Control Panel */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Graph Stage */}
        <div className="lg:col-span-8 flex flex-col gap-4">
          <div className="w-full aspect-[4/3] sm:aspect-[16/11] relative">
            {currentItem.dimension === '2d' ? (
              <Plot2D item={currentItem} customParams={params} showAsymptotes={true} />
            ) : (
              <Plot3D item={currentItem} customParams={params} />
            )}
          </div>

          {/* Governing Formula Banner */}
          <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-xl flex flex-col sm:flex-row items-center justify-between gap-3 shadow-lg">
            <div>
              <span className="text-xs text-slate-400 uppercase tracking-wider font-mono block mb-1">
                Active Equation:
              </span>
              <div className="text-lg text-teal-300 font-medium">
                <MathFormula latex={displayFormula} displayMode={false} />
              </div>
            </div>
            <div className="text-xs text-slate-400 max-w-sm sm:text-right">
              {currentItem.intuition.summary}
            </div>
          </div>
        </div>

        {/* Sidebar: Function Picker & Sliders */}
        <div className="lg:col-span-4 space-y-4">
          {/* Equation Library Selector */}
          <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl shadow-lg">
            <label className="text-xs font-mono text-slate-400 uppercase tracking-wider block mb-2">
              Select Equation:
            </label>
            <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
              {allItems.map((it) => (
                <button
                  key={it.id}
                  onClick={() => handleItemSelect(it)}
                  className={`w-full p-2.5 rounded-lg text-left text-xs font-medium transition flex items-center justify-between border ${
                    it.id === currentItem.id
                      ? 'bg-teal-500/10 border-teal-500/50 text-teal-200'
                      : 'bg-slate-950/50 border-slate-800/80 text-slate-300 hover:bg-slate-800/50 hover:border-slate-700'
                  }`}
                >
                  <span className="truncate">{it.title}</span>
                  <span className="text-[10px] font-mono text-slate-500 ml-2 uppercase">
                    {it.category}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Interactive Sliders (if item has parameters) */}
          {currentItem.parameters && Object.keys(currentItem.parameters).length > 0 ? (
            <div className="bg-slate-900/80 border border-slate-800 p-4 rounded-xl shadow-lg space-y-4">
              <div className="flex items-center gap-1.5 text-xs font-mono text-slate-300 font-semibold border-b border-slate-800 pb-2">
                <Sparkles className="w-4 h-4 text-teal-400" />
                <span>Transformation Parameters</span>
              </div>

              {Object.entries(currentItem.parameters).map(([key, pDef]) => {
                const currentVal = params[key] ?? pDef.default;
                return (
                  <div key={key} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs font-mono">
                      <span className="text-slate-400">{pDef.label}</span>
                      <span className="text-teal-300 font-semibold">{currentVal.toFixed(2)}</span>
                    </div>
                    <input
                      type="range"
                      min={pDef.min}
                      max={pDef.max}
                      step={pDef.step}
                      value={currentVal}
                      onChange={(e) => handleParamChange(key, parseFloat(e.target.value))}
                      className="w-full accent-teal-400 h-1.5 bg-slate-800 rounded-lg cursor-pointer"
                    />
                    <div className="flex justify-between text-[10px] font-mono text-slate-500">
                      <span>{pDef.min}</span>
                      <button
                        onClick={() => handleParamChange(key, pDef.default)}
                        className="text-slate-400 hover:text-teal-300 underline"
                      >
                        Reset ({pDef.default})
                      </button>
                      <span>{pDef.max}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-slate-900/40 border border-slate-800/60 p-4 rounded-xl text-xs text-slate-400 flex items-center gap-2">
              <Info className="w-4 h-4 text-slate-500 shrink-0" />
              <span>
                This canonical function has fixed geometric invariants. Orbit or zoom the viewport to inspect its structure.
              </span>
            </div>
          )}

          {/* Key Intuition Signatures */}
          <div className="bg-slate-900/60 border border-slate-800 p-4 rounded-xl shadow-lg">
            <h4 className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
              Geometric Signatures:
            </h4>
            <ul className="space-y-2 text-xs text-slate-300">
              {currentItem.intuition.keySignatures.map((sig, i) => (
                <li key={i} className="flex items-start gap-1.5">
                  <span className="text-teal-400 mt-0.5">•</span>
                  <span>{sig}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
